import { useEffect, useState } from "react";
import api from "@/lib/api";
import { useContent } from "@/context/ContentContext";

const CONTENT_GROUPS = [
    { id: "home", label: "Homepage", keys: [
        ["home.hero_eyebrow", "Hero eyebrow"],
        ["home.hero_title_line1", "Hero title — line 1"],
        ["home.hero_title_line2", "Hero title — line 2"],
        ["home.hero_body", "Hero body copy", "textarea"],
        ["home.hero_cta_primary", "Hero primary CTA"],
        ["home.hero_cta_secondary", "Hero secondary CTA"],
        ["home.hero_image", "Hero image URL"],
        ["home.stripe_tagline", "Stripe tagline"],
        ["home.edits_eyebrow", "Edits section eyebrow"],
        ["home.edits_title", "Edits section title"],
        ["home.pieces_eyebrow", "Pieces section eyebrow"],
        ["home.pieces_title", "Pieces section title"],
        ["home.sell_band_title", "Sell band title"],
        ["home.sell_band_body", "Sell band body", "textarea"],
        ["home.sell_band_cta", "Sell band CTA"],
        ["home.sell_band_image", "Sell band image URL"],
        ["home.trust_1_label", "Trust 1 — label"],
        ["home.trust_1_body", "Trust 1 — body"],
        ["home.trust_2_label", "Trust 2 — label"],
        ["home.trust_2_body", "Trust 2 — body"],
        ["home.trust_3_label", "Trust 3 — label"],
        ["home.trust_3_body", "Trust 3 — body"],
    ]},
    { id: "about", label: "About", keys: [
        ["about.eyebrow", "Eyebrow"],
        ["about.title", "Title"],
        ["about.lede", "Lede paragraph", "textarea"],
        ["about.image", "Hero image URL"],
        ["about.section_1_title", "Section 1 — title"],
        ["about.section_1_body", "Section 1 — body", "textarea"],
        ["about.section_2_title", "Section 2 — title"],
        ["about.section_2_body", "Section 2 — body", "textarea"],
        ["about.section_3_title", "Section 3 — title"],
        ["about.section_3_body", "Section 3 — body", "textarea"],
    ]},
    { id: "hiw", label: "How It Works", keys: [
        ["hiw.eyebrow", "Eyebrow"],
        ["hiw.title", "Title"],
        ["hiw.lede", "Lede", "textarea"],
        ["hiw.buying_title", "Buying — title"],
        ["hiw.buying_body", "Buying — body", "textarea"],
        ["hiw.selling_title", "Selling — title"],
        ["hiw.selling_body", "Selling — body", "textarea"],
        ["hiw.commission", "Commission line"],
        ["hiw.timelines", "Timelines line"],
        ["hiw.accepted_brands", "Accepted brands", "textarea"],
    ]},
    { id: "sell", label: "Sell With Us", keys: [
        ["sell.eyebrow", "Eyebrow"],
        ["sell.title", "Title"],
        ["sell.body", "Body", "textarea"],
        ["sell.commission_note", "Commission note"],
        ["sell.image", "Hero image URL"],
        ["sell.step_1_title", "Step 1 title"],
        ["sell.step_1_body", "Step 1 body", "textarea"],
        ["sell.step_2_title", "Step 2 title"],
        ["sell.step_2_body", "Step 2 body", "textarea"],
        ["sell.step_3_title", "Step 3 title"],
        ["sell.step_3_body", "Step 3 body", "textarea"],
        ["sell.step_4_title", "Step 4 title"],
        ["sell.step_4_body", "Step 4 body", "textarea"],
        ["sell.success_title", "Success — title"],
        ["sell.success_body", "Success — body", "textarea"],
    ]},
    { id: "contact", label: "Contact", keys: [
        ["contact.eyebrow", "Eyebrow"],
        ["contact.title", "Title"],
        ["contact.lede", "Lede", "textarea"],
        ["contact.reply_note", "Reply note"],
    ]},
    { id: "footer", label: "Footer", keys: [
        ["footer.newsletter_eyebrow", "Newsletter — eyebrow"],
        ["footer.newsletter_title", "Newsletter — title"],
        ["footer.newsletter_body", "Newsletter — body"],
        ["footer.tagline", "Footer tagline"],
        ["footer.location_label", "Footer location"],
    ]},
    { id: "seo", label: "SEO", keys: [
        ["seo.default_title", "Default page title"],
        ["seo.default_description", "Default meta description", "textarea"],
        ["seo.og_image", "Default OG image URL"],
    ]},
    { id: "global", label: "Global", keys: [
        ["global.contact_email", "Contact email"],
        ["global.location", "Location label"],
    ]},
];

export default function AdminContent() {
    const { content, refresh } = useContent();
    const [group, setGroup] = useState("home");
    const [local, setLocal] = useState({});
    const [saving, setSaving] = useState("");

    useEffect(() => {
        setLocal({ ...content });
    }, [content]);

    const activeGroup = CONTENT_GROUPS.find((g) => g.id === group);

    const saveKey = async (key) => {
        setSaving(key);
        try {
            await api.put(`/content/${key}`, { value: local[key] });
        } finally {
            setSaving("");
            await refresh();
        }
    };

    const resetKey = async (key) => {
        if (!window.confirm("Reset to the default copy?")) return;
        const { data } = await api.post(`/content/${key}/reset`);
        setLocal((l) => ({ ...l, [key]: data.value }));
        await refresh();
    };

    return (
        <div data-testid="admin-content">
            <div style={{ display: "flex", gap: 6, marginBottom: 24, flexWrap: "wrap" }}>
                {CONTENT_GROUPS.map((g) => (
                    <button key={g.id} className={`admin-tab ${group === g.id ? "active" : ""}`} onClick={() => setGroup(g.id)} data-testid={`admin-content-group-${g.id}`}>
                        {g.label}
                    </button>
                ))}
            </div>

            <div style={{ maxWidth: 720 }}>
                {activeGroup.keys.map(([k, label, type]) => (
                    <div key={k} className="field" data-testid={`admin-content-field-${k}`}>
                        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline" }}>
                            <label className="label">{label}</label>
                            <div style={{ display: "flex", gap: 12, fontFamily: "var(--font-sans)", fontSize: 10, letterSpacing: "0.24em", textTransform: "uppercase" }}>
                                <button type="button" onClick={() => resetKey(k)} style={{ background: "transparent", border: "none", cursor: "pointer", color: "var(--cocoa-soft)" }}>Reset</button>
                                <button type="button" onClick={() => saveKey(k)} disabled={saving === k} style={{ background: "transparent", border: "none", cursor: "pointer", color: "var(--cocoa)", fontWeight: 500 }} data-testid={`admin-content-save-${k}`}>
                                    {saving === k ? "Saving…" : "Save"}
                                </button>
                            </div>
                        </div>
                        {type === "textarea" ? (
                            <textarea className="input" rows="3" value={local[k] ?? ""} onChange={(e) => setLocal({ ...local, [k]: e.target.value })} />
                        ) : (
                            <input className="input" value={local[k] ?? ""} onChange={(e) => setLocal({ ...local, [k]: e.target.value })} />
                        )}
                        <div style={{ fontFamily: "monospace", fontSize: 10, color: "var(--sand)", marginTop: 4 }}>{k}</div>
                    </div>
                ))}
            </div>
        </div>
    );
}

// ============================================================
// SHIPPING ZONES ADMIN
